import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { clinicInfo, whyChooseUs, doctor } from '../data/mockData';

const About = () => {
  return (
    <div>
      {/* Hero Section */}
      <section style={{ 
        background: 'var(--bg-secondary)',
        padding: '80px 0',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <h1 className="hero-large" style={{ marginBottom: '16px' }}>
            About The Eternal Care
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            {clinicInfo.tagline}
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding">
        <div className="container">
          <div className="grid-two-column">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxkZW50YWwlMjBjbGluaWN8ZW58MHx8fHwxNzcwNzI3MTIzfDA&ixlib=rb-4.1.0&q=85"
                alt="The Eternal Care Clinic"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '0px'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Our Story
              </h2>
              <p className="body-large" style={{ 
                marginBottom: '20px',
                lineHeight: '1.8',
                color: 'var(--text-primary)'
              }}>
                The Eternal Care was founded with a simple yet profound vision: to transform dental 
                care into a painless, empathetic, and truly human experience. Located in the heart 
                of Mansarovar, Jaipur, we have been serving the community with dedication and compassion.
              </p>
              <p className="body-large" style={{ 
                marginBottom: '20px',
                lineHeight: '1.8',
                color: 'var(--text-primary)'
              }}>
                Under the expert leadership of Dr. Parmila Mahala, our clinic combines advanced dental 
                technology with a patient-first approach. We understand that visiting the dentist can 
                be anxiety-inducing, which is why we've made painless dentistry our top priority.
              </p>
              <p className="body-large" style={{ 
                marginBottom: '32px',
                lineHeight: '1.8',
                color: 'var(--text-primary)'
              }}>
                Every member of our team is committed to providing care that goes beyond clinical 
                excellence—we focus on building trust, ensuring comfort, and delivering results that 
                restore not just your smile, but your confidence.
              </p>
              <Link to="/doctor" className="btn-secondary" style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                Meet Our Doctor
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section style={{ background: 'var(--bg-secondary)' }} className="section-padding">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '48px'
          }}>
            <div>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Our Vision
              </h2>
              <p className="body-large" style={{ 
                lineHeight: '1.8',
                color: 'var(--text-primary)'
              }}>
                To provide painless dental treatments through advanced technology and an empathetic, 
                human-centered approach that prioritizes patient comfort, trust, and long-term oral health.
              </p>
            </div>
            
            <div>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Our Mission
              </h2>
              <p className="body-large" style={{ 
                lineHeight: '1.8',
                color: 'var(--text-primary)'
              }}>
                To make quality dental care accessible, comfortable, and delivered with genuine compassion. 
                We strive to transform every patient's experience through painless procedures, advanced 
                technology, and personalized care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>
              Why Choose Us
            </h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Excellence in dental care through advanced technology and human touch
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {whyChooseUs.map((item, index) => (
              <div 
                key={index}
                style={{
                  padding: '32px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '0px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                  {item.title}
                </h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section style={{ background: 'var(--bg-secondary)' }} className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>
              Our Core Values
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '32px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {[
              {
                title: 'Compassion',
                description: 'Every patient deserves empathy, understanding, and gentle care'
              },
              {
                title: 'Excellence',
                description: 'Committed to the highest standards in dental treatment and technology'
              },
              {
                title: 'Trust',
                description: 'Building lasting relationships through transparency and reliability'
              },
              {
                title: 'Innovation',
                description: 'Continuously adopting advanced techniques for better patient outcomes'
              }
            ].map((value, index) => (
              <div 
                key={index}
                style={{
                  padding: '32px',
                  background: 'var(--bg-primary)',
                  borderRadius: '0px',
                  border: '1px solid var(--border-light)',
                  textAlign: 'center'
                }}
              >
                <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                  {value.title}
                </h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container">
          <div style={{
            background: 'var(--interactive-base)',
            color: 'var(--bg-primary)',
            padding: '64px 48px',
            textAlign: 'center',
            borderRadius: '0px'
          }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px', color: 'var(--bg-primary)' }}>
              Experience the Difference
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '32px',
              maxWidth: '600px',
              margin: '0 auto 32px',
              opacity: 0.9
            }}>
              Join thousands of satisfied patients who have experienced painless, 
              compassionate dental care at The Eternal Care
            </p>
            <Link 
              to="/book-appointment" 
              className="btn-primary"
              style={{
                background: 'var(--bg-primary)',
                color: 'var(--interactive-base)',
                border: '1px solid var(--bg-primary)'
              }}
            >
              Book Your Appointment
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
